import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/App';
import {
  CreditCard, Loader2, AlertCircle, Calendar, Repeat, ShoppingBag,
  CheckCircle, History,
} from 'lucide-react';

const PLAN_ICONS = {
  practitioner_monthly: Calendar,
  practitioner_yearly:  Repeat,
  vendor_subscription:  ShoppingBag,
};

const AdminSubscriptionPricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyPlan, setHistoryPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/subscription-pricing`, { headers: headers() });
      setPlans(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscription pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setEditAmount(plan.current_amount?.toString() || plan.default_amount.toString());
    setEditNotes('');
  };

  const handleSave = async () => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount < 0.5 || amount > 9999) {
      toast.error('Price must be between $0.50 and $9,999');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/subscription-pricing/${editingPlan.plan_key}`,
        { new_amount: amount, notes: editNotes || null },
        { headers: headers() }
      );
      toast.success(r.data.message);
      setEditingPlan(null);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update price');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (plan) => {
    setHistoryPlan(plan);
    setLoadingHistory(true);
    try {
      const r = await axios.get(
        `${API}/admin/subscription-pricing/${plan.plan_key}/history`,
        { headers: headers() }
      );
      setHistory(r.data);
    } catch (err) {
      toast.error('Failed to load price history');
    } finally {
      setLoadingHistory(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-subscription-pricing">
      <div>
        <h2 className="text-2xl font-bold text-white">Subscription Pricing</h2>
        <p className="text-sm text-gray-400 mt-1">
          Adjust the cost of recurring practitioner subscriptions.
          Existing subscribers will keep their current price (grandfathered); new subscribers will be charged the updated amount.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const Icon = PLAN_ICONS[plan.plan_key] || CreditCard;
          const stripeOk = plan.stripe_status === 'ok';
          return (
            <Card
              key={plan.plan_key}
              className="bg-slate-800/60 border-slate-700"
              data-testid={`sub-pricing-card-${plan.plan_key}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-cyan-600/20">
                      <Icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white">{plan.label}</h3>
                      <p className="text-xs text-gray-400">{plan.description}</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Current price</div>
                  {stripeOk ? (
                    <div className="text-3xl font-bold text-white">
                      ${plan.current_amount?.toFixed(2) ?? '—'}
                      <span className="text-sm text-gray-400 ml-2">
                        / {plan.interval}
                      </span>
                    </div>
                  ) : (
                    <div className="text-amber-300 text-sm flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Not configured in Stripe
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" data-testid={`sub-pricing-active-${plan.plan_key}`}>
                    {plan.active_subscribers} active subscriber{plan.active_subscribers === 1 ? '' : 's'}
                  </Badge>
                  {plan.last_updated && (
                    <Badge variant="outline" className="text-xs">
                      Updated {new Date(plan.last_updated).toLocaleDateString()}
                    </Badge>
                  )}
                </div>

                {plan.notes && (
                  <div className="text-xs text-gray-400 bg-slate-900/50 rounded p-2">
                    <span className="font-medium text-gray-300">Last note:</span> {plan.notes}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    onClick={() => openEdit(plan)}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid={`sub-pricing-edit-${plan.plan_key}`}
                  >
                    Edit Price
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => openHistory(plan)}
                    title="View price history"
                    data-testid={`sub-pricing-history-${plan.plan_key}`}
                  >
                    <History className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editingPlan} onOpenChange={(open) => !open && setEditingPlan(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit price · {editingPlan?.label}
            </DialogTitle>
            <DialogDescription>
              A new Stripe Price will be created. Existing subscribers stay on the previous price.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-amount">New amount (USD)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="9999"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  className="pl-7"
                  data-testid="sub-pricing-amount-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Charged every {editingPlan?.interval}. Default: ${editingPlan?.default_amount?.toFixed(2)}.
              </p>
            </div>
            <div>
              <Label htmlFor="edit-notes">Note (optional)</Label>
              <Textarea
                id="edit-notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="e.g., Q1 promotional pricing"
                rows={2}
                data-testid="sub-pricing-notes-input"
              />
            </div>
            {editingPlan?.active_subscribers > 0 && (
              <div className="flex items-start gap-2 text-amber-200 bg-amber-900/30 rounded p-2 text-xs">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  {editingPlan.active_subscribers} active subscriber(s) will keep paying the
                  current ${editingPlan.current_amount?.toFixed(2)} price. Only NEW subscribers
                  will be charged the updated amount.
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPlan(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="sub-pricing-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save new price
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyPlan} onOpenChange={(open) => !open && setHistoryPlan(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Price history · {historyPlan?.label}
            </DialogTitle>
          </DialogHeader>
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-gray-400 text-sm py-6 text-center">No price changes recorded yet.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="bg-slate-800/50 rounded p-3 text-sm">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="text-white font-semibold">${h.amount.toFixed(2)}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(h.changed_at).toLocaleString()}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs font-mono">{h.new_price_id}</Badge>
                  </div>
                  {h.notes && <div className="text-gray-300 text-xs mt-2 italic">{h.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptionPricing;
